import React, { useState, useEffect, useRef } from 'react';
import { Destination, DestinationUpdateRequest, InventoryDestination } from '../../types';
import { Plus, X } from 'lucide-react';
import { HtmlEditor } from '../common/HtmlEditor';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

interface UpdateDestinationFormProps {
  destination: Destination;
  onSubmit: (data: DestinationUpdateRequest) => void;
  onCancel: () => void;
  loading: boolean;
}

export const UpdateDestinationForm: React.FC<UpdateDestinationFormProps> = ({
  destination,
  onSubmit,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState<DestinationUpdateRequest>({
    title: destination.title || '',
    slug: destination.slug || '',
    summary: destination.summary || '',
    description: destination.description || '',
    whyJoin: destination.whyJoin || [],
    highlights: destination.highlights || [],
    region: destination.region || '',
    state: destination.state || '',
    country: destination.country || '',
    tags: destination.tags || [],
    published: destination.published || false,
    faqs: destination.faqs || [],
    itineraries: destination.itineraries || [],
    meta: destination.meta,
    geolocation: destination.geolocation,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialTitleRef = useRef(destination.title || '');

  const { confirmNavigation } = useUnsavedChanges({
    isDirty: formData.title !== initialTitleRef.current,
  });

  const [newWhyJoin, setNewWhyJoin] = useState('');
  const [newHighlight, setNewHighlight] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newFaqQuestion, setNewFaqQuestion] = useState('');
  const [newFaqAnswer, setNewFaqAnswer] = useState('');
  const [newMetaKeyword, setNewMetaKeyword] = useState('');
  const [editingItineraryIndex, setEditingItineraryIndex] = useState<number | null>(null);
  const [newItinerary, setNewItinerary] = useState<InventoryDestination>({
    id: '',
    slug: '',
    image: '',
    title: '',
    description: '',
    duration: '',
    price: 0,
    maxPeople: 0,
  });

  useEffect(() => {
    setFormData({
      title: destination.title || '',
      slug: destination.slug || '',
      summary: destination.summary || '',
      description: destination.description || '',
      whyJoin: destination.whyJoin || [],
      highlights: destination.highlights || [],
      region: destination.region || '',
      state: destination.state || '',
      country: destination.country || '',
      tags: destination.tags || [],
      published: destination.published || false,
      faqs: destination.faqs || [],
      itineraries: destination.itineraries || [],
      meta: destination.meta,
      geolocation: destination.geolocation,
    });
  }, [destination]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof DestinationUpdateRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const addWhyJoin = () => {
    if (newWhyJoin.trim()) {
      setFormData(prev => ({
        ...prev,
        whyJoin: [...(prev.whyJoin || []), newWhyJoin.trim()],
      }));
      setNewWhyJoin('');
    }
  };

  const removeWhyJoin = (index: number) => {
    setFormData(prev => ({
      ...prev,
      whyJoin: prev.whyJoin?.filter((_, i) => i !== index) || [],
    }));
  };

  const addHighlight = () => {
    if (newHighlight.trim()) {
      setFormData(prev => ({
        ...prev,
        highlights: [...(prev.highlights || []), newHighlight.trim()],
      }));
      setNewHighlight('');
    }
  };

  const removeHighlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights?.filter((_, i) => i !== index) || [],
    }));
  };

  const addTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const removeTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || [],
    }));
  };

  // FAQ management
  const addFaq = () => {
    if (newFaqQuestion.trim() && newFaqAnswer.trim()) {
      const newFaq: any = {
        question: newFaqQuestion.trim(),
        answer: newFaqAnswer.trim(),
        displayOrder: (formData.faqs?.length || 0) + 1,
        visible: true,
      };
      setFormData(prev => ({
        ...prev,
        faqs: [...(prev.faqs || []), newFaq],
      }));
      setNewFaqQuestion('');
      setNewFaqAnswer('');
    }
  };

  const removeFaq = (index: number) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs?.filter((_, i) => i !== index) || [],
    }));
  };

  // Meta/SEO management
  const updateMeta = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      meta: {
        ...prev.meta,
        [field]: value,
      },
    }));
  };

  const addMetaKeyword = () => {
    if (newMetaKeyword.trim()) {
      setFormData(prev => ({
        ...prev,
        meta: {
          ...prev.meta,
          metaKeywords: [...(prev.meta?.metaKeywords || []), newMetaKeyword.trim()],
        },
      }));
      setNewMetaKeyword('');
    }
  };

  const removeMetaKeyword = (index: number) => {
    setFormData(prev => ({
      ...prev,
      meta: {
        ...prev.meta,
        metaKeywords: prev.meta?.metaKeywords?.filter((_, i) => i !== index) || [],
      },
    }));
  };

  // Geolocation management
  const updateGeolocation = (field: 'x' | 'y', value: number) => {
    setFormData(prev => ({
      ...prev,
      geolocation: {
        type: 'Point',
        coordinates: field === 'x' 
          ? [value, prev.geolocation?.coordinates?.[1] || 0]
          : [prev.geolocation?.coordinates?.[0] || 0, value],
      },
    }));
  };

  // Itineraries management
  const addItinerary = () => {
    if (newItinerary.title?.trim()) {
      setFormData(prev => ({
        ...prev,
        itineraries: [...(prev.itineraries || []), { ...newItinerary }],
      }));
      setNewItinerary({
        id: '',
        slug: '',
        image: '',
        title: '',
        description: '',
        duration: '',
        price: 0,
        maxPeople: 0,
      });
    }
  };

  const updateItinerary = (index: number) => {
    if (editingItineraryIndex !== null && newItinerary.title?.trim()) {
      setFormData(prev => ({
        ...prev,
        itineraries: prev.itineraries?.map((item, i) => 
          i === index ? { ...newItinerary } : item
        ) || [],
      }));
      setEditingItineraryIndex(null);
      setNewItinerary({
        id: '',
        slug: '',
        image: '',
        title: '',
        description: '',
        duration: '',
        price: 0,
        maxPeople: 0,
      });
    }
  };

  const removeItinerary = (index: number) => {
    setFormData(prev => ({
      ...prev,
      itineraries: prev.itineraries?.filter((_, i) => i !== index) || [],
    }));
  };

  const startEditItinerary = (index: number) => {
    const itinerary = formData.itineraries?.[index];
    if (itinerary) {
      setNewItinerary({ ...itinerary });
      setEditingItineraryIndex(index);
    }
  };

  const cancelEditItinerary = () => {
    setEditingItineraryIndex(null);
    setNewItinerary({
      id: '',
      slug: '',
      image: '',
      title: '',
      description: '',
      duration: '',
      price: 0,
      maxPeople: 0,
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => confirmNavigation(onCancel)}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4"
              disabled={loading}
            >
              <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Destinations
            </button>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Destination</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Update destination information
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Basic Information
            </h3>

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter destination title"
                disabled={loading}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Slug
              </label>
              <input
                type="text"
                id="slug"
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="manali-himachal-pradesh"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="summary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Summary
              </label>
              <textarea
                id="summary"
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Brief summary of the destination"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description (HTML)
              </label>
              <HtmlEditor
                value={formData.description || ''}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Enter detailed description of the destination (HTML supported)"
                disabled={loading}
                className="w-full"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Location
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="India"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Himachal Pradesh"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Region
                </label>
                <input
                  type="text"
                  id="region"
                  value={formData.region}
                  onChange={(e) => handleInputChange('region', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="North India"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Why Join */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Why Join
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newWhyJoin}
                onChange={(e) => setNewWhyJoin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addWhyJoin())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Add reason to visit"
                disabled={loading}
              />
              <button
                type="button"
                onClick={addWhyJoin}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {formData.whyJoin && formData.whyJoin.length > 0 && (
              <div className="space-y-2">
                {formData.whyJoin.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-900 dark:text-white">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeWhyJoin(index)}
                      className="text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Highlights */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Highlights
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newHighlight}
                onChange={(e) => setNewHighlight(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Add highlight"
                disabled={loading}
              />
              <button
                type="button"
                onClick={addHighlight}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {formData.highlights && formData.highlights.length > 0 && (
              <div className="space-y-2">
                {formData.highlights.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    <span className="text-sm text-gray-900 dark:text-white">{item}</span>
                    <button
                      type="button"
                      onClick={() => removeHighlight(index)}
                      className="text-red-600 hover:text-red-800"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Tags
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Add tag"
                disabled={loading}
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={loading}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Itineraries */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Itineraries
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Inventory ID
                  </label>
                  <input
                    type="text"
                    value={newItinerary.id || ''}
                    onChange={(e) => setNewItinerary(prev => ({ ...prev, id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter inventory ID"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={newItinerary.slug || ''}
                    onChange={(e) => setNewItinerary(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter slug"
                    disabled={loading}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newItinerary.title || ''}
                  onChange={(e) => setNewItinerary(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter itinerary title"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newItinerary.description || ''}
                  onChange={(e) => setNewItinerary(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter description"
                  disabled={loading}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={newItinerary.image || ''}
                    onChange={(e) => setNewItinerary(prev => ({ ...prev, image: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Image URL"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Duration
                  </label>
                  <input
                    type="text"
                    value={newItinerary.duration || ''}
                    onChange={(e) => setNewItinerary(prev => ({ ...prev, duration: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., 3 Days 2 Nights"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Price
                  </label>
                  <input
                    type="number"
                    value={newItinerary.price || ''}
                    onChange={(e) => setNewItinerary(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Price"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max People
                  </label>
                  <input
                    type="number"
                    value={newItinerary.maxPeople || ''}
                    onChange={(e) => setNewItinerary(prev => ({ ...prev, maxPeople: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Max people"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                {editingItineraryIndex !== null ? (
                  <>
                    <button
                      type="button"
                      onClick={() => updateItinerary(editingItineraryIndex)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                      disabled={loading || !newItinerary.title?.trim()}
                    >
                      <span>Update Itinerary</span>
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditItinerary}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={addItinerary}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                    disabled={loading || !newItinerary.title?.trim()}
                  >
                    <Plus className="h-5 w-5" />
                    <span>Add Itinerary</span>
                  </button>
                )}
              </div>
            </div>
            {formData.itineraries && formData.itineraries.length > 0 && (
              <div className="space-y-3">
                {formData.itineraries.map((itinerary, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {itinerary.image && (
                            <img 
                              src={itinerary.image} 
                              alt={itinerary.title || 'Itinerary'} 
                              className="w-16 h-16 object-cover rounded"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                              {itinerary.title || 'Untitled Itinerary'}
                            </h4>
                            {itinerary.id && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">ID: {itinerary.id}</p>
                            )}
                            {itinerary.slug && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">Slug: {itinerary.slug}</p>
                            )}
                          </div>
                        </div>
                        {itinerary.description && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{itinerary.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
                          {itinerary.duration && (
                            <span>Duration: {itinerary.duration}</span>
                          )}
                          {itinerary.price !== undefined && itinerary.price > 0 && (
                            <span>Price: ₹{itinerary.price}</span>
                          )}
                          {itinerary.maxPeople !== undefined && itinerary.maxPeople > 0 && (
                            <span>Max People: {itinerary.maxPeople}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          type="button"
                          onClick={() => startEditItinerary(index)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          disabled={loading}
                          title="Edit"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItinerary(index)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          disabled={loading}
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* FAQs */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              FAQs
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Question *
                </label>
                <input
                  type="text"
                  value={newFaqQuestion}
                  onChange={(e) => setNewFaqQuestion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter FAQ question"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Answer *
                </label>
                <textarea
                  value={newFaqAnswer}
                  onChange={(e) => setNewFaqAnswer(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter FAQ answer"
                  disabled={loading}
                />
              </div>
              <button
                type="button"
                onClick={addFaq}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                disabled={loading || !newFaqQuestion.trim() || !newFaqAnswer.trim()}
              >
                <Plus className="h-5 w-5" />
                <span>Add FAQ</span>
              </button>
            </div>
            {formData.faqs && formData.faqs.length > 0 && (
              <div className="space-y-3">
                {formData.faqs.map((faq, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Q: {faq.question}</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">A: {faq.answer}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFaq(index)}
                        className="ml-2 text-red-600 hover:text-red-800"
                        disabled={loading}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SEO / Meta */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              SEO / Meta Information
            </h3>
            
            {/* Basic SEO */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Basic SEO</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meta Title
                </label>
                <input
                  type="text"
                  value={formData.meta?.metaTitle || ''}
                  onChange={(e) => updateMeta('metaTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="SEO meta title"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meta Description
                </label>
                <textarea
                  value={formData.meta?.metaDescription || ''}
                  onChange={(e) => updateMeta('metaDescription', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="SEO meta description"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meta Keywords
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMetaKeyword}
                    onChange={(e) => setNewMetaKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMetaKeyword())}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Add keyword"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={addMetaKeyword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                {formData.meta?.metaKeywords && formData.meta.metaKeywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.meta.metaKeywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm"
                      >
                        {keyword}
                        <button
                          type="button"
                          onClick={() => removeMetaKeyword(index)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          disabled={loading}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Canonical URL
                </label>
                <input
                  type="url"
                  value={formData.meta?.canonicalUrl || ''}
                  onChange={(e) => updateMeta('canonicalUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://www.bozapp.in/destinations/slug"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Open Graph */}
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Open Graph (Facebook, WhatsApp, LinkedIn)</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  OG Title
                </label>
                <input
                  type="text"
                  value={formData.meta?.ogTitle || ''}
                  onChange={(e) => updateMeta('ogTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Open Graph title"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  OG Description
                </label>
                <textarea
                  value={formData.meta?.ogDescription || ''}
                  onChange={(e) => updateMeta('ogDescription', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Open Graph description"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  OG Image URL
                </label>
                <input
                  type="url"
                  value={formData.meta?.ogImage || ''}
                  onChange={(e) => updateMeta('ogImage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://example.com/og-image.jpg"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  OG Type
                </label>
                <select
                  value={formData.meta?.ogType || 'website'}
                  onChange={(e) => updateMeta('ogType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                >
                  <option value="website">Website</option>
                  <option value="article">Article</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  OG URL
                </label>
                <input
                  type="url"
                  value={formData.meta?.ogUrl || ''}
                  onChange={(e) => updateMeta('ogUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://www.bozapp.in/destinations/slug"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Twitter Card */}
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Twitter Card</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Twitter Card Type
                </label>
                <select
                  value={formData.meta?.twitterCard || 'summary'}
                  onChange={(e) => updateMeta('twitterCard', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                >
                  <option value="summary">Summary</option>
                  <option value="summary_large_image">Summary Large Image</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Twitter Title
                </label>
                <input
                  type="text"
                  value={formData.meta?.twitterTitle || ''}
                  onChange={(e) => updateMeta('twitterTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Twitter card title"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Twitter Description
                </label>
                <textarea
                  value={formData.meta?.twitterDescription || ''}
                  onChange={(e) => updateMeta('twitterDescription', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Twitter card description"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Twitter Image URL
                </label>
                <input
                  type="url"
                  value={formData.meta?.twitterImage || ''}
                  onChange={(e) => updateMeta('twitterImage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://example.com/twitter-image.jpg"
                  disabled={loading}
                />
              </div>
            </div>

            {/* JSON-LD Structured Data */}
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">JSON-LD Structured Data</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Structured Type
                </label>
                <select
                  value={formData.meta?.structuredType || ''}
                  onChange={(e) => updateMeta('structuredType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  disabled={loading}
                >
                  <option value="">Select type</option>
                  <option value="TouristDestination">Tourist Destination</option>
                  <option value="TouristTrip">Tourist Trip</option>
                </select>
              </div>
            </div>

            {/* SEO Optimization Flags */}
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">SEO Optimization Flags</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.meta?.indexable ?? true}
                    onChange={(e) => updateMeta('indexable', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Indexable (allow search engine indexing)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.meta?.followLinks ?? true}
                    onChange={(e) => updateMeta('followLinks', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Follow Links (allow link following)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.meta?.aiDiscoverable ?? true}
                    onChange={(e) => updateMeta('aiDiscoverable', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    AI Discoverable (allow AI crawlers like ChatGPT, Bing, etc.)
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Language
                  </label>
                  <input
                    type="text"
                    value={formData.meta?.language || ''}
                    onChange={(e) => updateMeta('language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="en-IN"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Geolocation */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
              Geolocation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Longitude (X)
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.geolocation?.coordinates?.[0] || ''}
                  onChange={(e) => updateGeolocation('x', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="76.695"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Latitude (Y)
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.geolocation?.coordinates?.[1] || ''}
                  onChange={(e) => updateGeolocation('y', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="11.4064"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Published */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => handleInputChange('published', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={loading}
            />
            <label htmlFor="published" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Publish destination
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => confirmNavigation(onCancel)}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Destination'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

